import { SERVICES_API_BASE_URL } from './apiConfig';
import {
  ApiError,
  CrmProvider,
  Lead,
  LeadCreateDto,
  LeadListQuery,
  LeadNotePayload,
  LeadTimelineEvent,
  NewsletterSubscriber,
  UpdateLeadStatusDto,
  PagedResult,
  FormsDashboardMetrics,
  NewsletterSubscribeRequest,
  NewsletterUnsubscribeRequest,
} from '../types/leadCapture';

const API_BASE = `${(SERVICES_API_BASE_URL || '/api').replace(/\/+$/, '')}`;

const buildQueryString = (params: Record<string, string | number | undefined>) => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value));
    }
  });

  const query = search.toString();
  return query ? `?${query}` : '';
};

const getAuthHeaders = (headers: Record<string, string> = {}) => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const toApiError = async (response: Response): Promise<ApiError> => {
  const body = await response.text();
  let details: Record<string, unknown> | undefined;
  let code = 'REQUEST_FAILED';

  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed === 'object') {
      details = parsed;
      if (typeof parsed.code === 'string') {
        code = parsed.code;
      }
    }
  } catch {
    // Ignore invalid JSON payloads
  }

  const message =
    details && typeof details.message === 'string'
      ? details.message
      : `Request failed with status ${response.status}.`;

  return {
    message,
    status: response.status,
    code:
      response.status >= 500
        ? 'SERVER_ERROR'
        : response.status === 429
          ? 'RATE_LIMITED'
          : response.status === 401
            ? 'UNAUTHORIZED'
            : response.status === 403
              ? 'FORBIDDEN'
              : code,
    details,
  };
};

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...getAuthHeaders(),
      ...(init.headers ?? {}),
    },
  });

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export const leadsApi = {
  /**
   * Get paginated list of leads
   * GET /api/leads
   */
  getLeads: async (params: LeadListQuery = {}): Promise<PagedResult<Lead>> => {
    const query = buildQueryString({
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      status: params.status && params.status !== 'ALL' ? params.status : undefined,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
    });

    return request<PagedResult<Lead>>(`/api/leads${query}`);
  },

  /**
   * Get single lead by ID
   * GET /api/leads/{leadId}
   */
  getLead: async (leadId: string): Promise<Lead> => {
    return request<Lead>(`/api/leads/${leadId}`);
  },

  /**
   * Create new lead
   * POST /api/leads
   */
  createLead: async (payload: LeadCreateDto): Promise<Lead> => {
    return request<Lead>('/api/leads', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Update lead status (204 No Content response)
   * PATCH /api/leads/{leadId}/status
   */
  updateLeadStatus: async (leadId: string, payload: UpdateLeadStatusDto): Promise<void> => {
    return request<void>(`/api/leads/${leadId}/status`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Get lead timeline events
   * GET /api/leads/{leadId}/timeline
   */
  getLeadTimeline: async (leadId: string): Promise<LeadTimelineEvent[]> => {
    return request<LeadTimelineEvent[]>(`/api/leads/${leadId}/timeline`);
  },

  /**
   * Add note to lead (204 No Content response)
   * POST /api/leads/{leadId}/notes
   */
  addLeadNote: async (leadId: string, payload: LeadNotePayload): Promise<void> => {
    return request<void>(`/api/leads/${leadId}/notes`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Get dashboard metrics
   * GET /api/leads/dashboard
   */
  getDashboardMetrics: async (): Promise<FormsDashboardMetrics> => {
    return request<FormsDashboardMetrics>('/api/leads/dashboard');
  },
};

export const newsletterApi = {
  /**
   * Get newsletter subscribers
   * GET /api/newsletter/subscribers
   */
  getSubscribers: async (): Promise<PagedResult<NewsletterSubscriber>> => {
    return request<PagedResult<NewsletterSubscriber>>('/api/newsletter/subscribers');
  },

  /**
   * Subscribe email to newsletter (204 No Content response)
   * POST /api/newsletter/subscribe
   */
  subscribe: async (payload: NewsletterSubscribeRequest): Promise<void> => {
    return request<void>('/api/newsletter/subscribe', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /** POST /api/newsletter/unsubscribe */
  unsubscribe: async (payload: NewsletterUnsubscribeRequest): Promise<void> => {
    return request<void>('/api/newsletter/unsubscribe', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

export const crmApi = {
  /**
   * Get CRM provider status
   * GET /api/crm/providers
   */
  getProviders: async (): Promise<CrmProvider[]> => {
    return request<CrmProvider[]>('/api/crm/providers');
  },
};
